import axiosInstance from './axios-client';
import type { 
  Storico, 
  StoricoSummary, 
  CreateStoricoDto, 
  UpdateStoricoDto, 
  StoricoFilter, 
  PagedResult 
} from '../types/storico-types';

const STORICO_BASE_URL = '/api/storico';

export const storicoService = {
  /**
   * Ottiene la lista paginata dei record storici
   */
  getAll: async (
    pageNumber: number = 1,
    pageSize: number = 20,
    searchTerm?: string
  ): Promise<PagedResult<Storico>> => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }
    
    const response = await axiosInstance.get<{ data: PagedResult<Storico> }>(
      `${STORICO_BASE_URL}?${params.toString()}`
    );
    return response.data.data;
  },

  /**
   * Ottiene record storici con filtri avanzati
   */
  getFiltered: async (filter: StoricoFilter): Promise<PagedResult<Storico>> => {
    const response = await axiosInstance.post<{ data: PagedResult<Storico> }>(
      `${STORICO_BASE_URL}/filter`,
      filter
    );
    return response.data.data;
  },

  /**
   * Ottiene un singolo record per ID
   */
  getById: async (id: string): Promise<Storico> => {
    const response = await axiosInstance.get<{ data: Storico }>(
      `${STORICO_BASE_URL}/${id}`
    );
    return response.data.data;
  },

  /**
   * Cerca record per nome decoro
   */
  searchByNomeDecoro: async (searchTerm: string): Promise<Storico[]> => {
    const response = await axiosInstance.get<{ data: Storico[] }>(
      `${STORICO_BASE_URL}/search/${encodeURIComponent(searchTerm)}`
    );
    return response.data.data;
  },

  /**
   * Ottiene record per categoria
   */
  getByCategoria: async (categoria: string): Promise<Storico[]> => {
    const response = await axiosInstance.get<{ data: Storico[] }>(
      `${STORICO_BASE_URL}/categoria/${encodeURIComponent(categoria)}`
    );
    return response.data.data;
  },

  /**
   * Ottiene record per stato progetto
   */
  getByStatoProgetto: async (statoProgetto: string): Promise<Storico[]> => {
    const response = await axiosInstance.get<{ data: Storico[] }>(
      `${STORICO_BASE_URL}/stato/${encodeURIComponent(statoProgetto)}`
    );
    return response.data.data;
  },

  /**
   * Ottiene record per numero decoro
   */
  getByNumDecoro: async (numDecoro: string): Promise<Storico[]> => {
    const response = await axiosInstance.get<{ data: Storico[] }>(
      `${STORICO_BASE_URL}/numero/${encodeURIComponent(numDecoro)}`
    );
    return response.data.data;
  },

  /**
   * Ottiene record per range di date
   */
  getByDateRange: async (startDate?: string, endDate?: string): Promise<Storico[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await axiosInstance.get<{ data: Storico[] }>(
      `${STORICO_BASE_URL}/daterange?${params.toString()}`
    );
    return response.data.data;
  },

  /**
   * Ottiene tutte le categorie distinte
   */
  getCategorie: async (): Promise<string[]> => {
    const response = await axiosInstance.get<{ data: string[] }>(
      `${STORICO_BASE_URL}/metadata/categorie`
    );
    return response.data.data;
  },

  /**
   * Ottiene tutti gli stati progetto distinti
   */
  getStatiProgetto: async (): Promise<string[]> => {
    const response = await axiosInstance.get<{ data: string[] }>(
      `${STORICO_BASE_URL}/metadata/stati`
    );
    return response.data.data;
  },

  /**
   * Ottiene le tipologie statistiche distinte (es. "Bozzetto", "Campione", "-" per Produzione)
   */
  getTipologie: async (): Promise<string[]> => {
    const response = await axiosInstance.get<{ data: string[] }>(
      `${STORICO_BASE_URL}/metadata/tipologie`
    );
    return response.data.data;
  },

  /**
   * Ottiene gli anni distinti presenti nello storico (DataCompletamento o FineSviluppoImpiantiPellicole)
   */
  getAnni: async (): Promise<number[]> => {
    const response = await axiosInstance.get<{ data: number[] }>(
      `${STORICO_BASE_URL}/metadata/anni`
    );
    return response.data.data;
  },

  /**
   * Ottiene il conteggio totale dei record
   */
  getCount: async (): Promise<number> => {
    const response = await axiosInstance.get<{ data: { count: number } }>(
      `${STORICO_BASE_URL}/count`
    );
    return response.data.data.count;
  },

  /**
   * Import dati da file Excel (solo admin)
   */
  importFromExcel: async (file: File): Promise<number> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post<{ data: number }>(
      `${STORICO_BASE_URL}/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Cancella tutti i record (solo admin)
   */
  clearAll: async (): Promise<boolean> => {
    const response = await axiosInstance.delete<{ data: boolean }>(
      `${STORICO_BASE_URL}/clear`
    );
    return response.data.data;
  },

  /**
   * Crea un nuovo record (solo admin)
   */
  create: async (data: CreateStoricoDto): Promise<Storico> => {
    const response = await axiosInstance.post<{ data: Storico }>(
      STORICO_BASE_URL,
      data
    );
    return response.data.data;
  },

  /**
   * Aggiorna un record (solo admin)
   */
  update: async (id: string, data: UpdateStoricoDto): Promise<Storico> => {
    const response = await axiosInstance.put<{ data: Storico }>(
      `${STORICO_BASE_URL}/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Elimina un record (solo admin)
   */
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${STORICO_BASE_URL}/${id}`);
  },
};
